import React from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  Cards,
  Badge,
  Button,
  Alert,
  TextContent,
  Icon
} from '@cloudscape-design/components';

function WhatsNextSection() {
  const expansionIdeas = [
    {
      id: 'portfolio-intelligence',
      title: 'Portfolio Intelligence Platform',
      description: 'Scale from single-company analysis to comprehensive portfolio monitoring across your entire investment universe.',
      category: 'Investment Management',
      complexity: 'High',
      keyFeatures: [
        'Cross-portfolio risk assessment and correlation analysis',
        'Automated ESG compliance monitoring across holdings',
        'Real-time ASIC and APRA filing impact analysis',
        'Comparative performance benchmarking'
      ],
      businessValue: [
        'Reduce research time by 70% across portfolio companies',
        'Early identification of portfolio-wide risk factors',
        'Automated compliance reporting for APRA and ASIC requirements',
        'Enhanced due diligence capabilities for new investments'
      ]
    },
    {
      id: 'regulatory-intelligence',
      title: 'Regulatory Intelligence Engine',
      description: 'Transform regulatory monitoring from reactive compliance to proactive strategic advantage.',
      category: 'Risk & Compliance',
      complexity: 'High',
      keyFeatures: [
        'Multi-jurisdiction regulatory change detection',
        'Impact assessment across business lines',
        'Automated compliance gap identification',
        'Regulatory scenario modeling and stress testing'
      ],
      businessValue: [
        'Proactive compliance strategy development',
        'Reduced regulatory risk exposure',
        'Streamlined audit preparation and reporting',
        'Competitive advantage through early regulatory insights'
      ]
    },
    {
      id: 'market-surveillance',
      title: 'Market Surveillance & Early Warning',
      description: 'Deploy continuous market monitoring to identify opportunities and risks before they impact your business.',
      category: 'Market Intelligence',
      complexity: 'Medium',
      keyFeatures: [
        'Real-time ASIC filing analysis and alerts',
        'Market sentiment tracking from multiple data sources',
        'Competitor intelligence and benchmarking',
        'Macroeconomic trend correlation analysis'
      ],
      businessValue: [
        'First-mover advantage on market opportunities',
        'Early risk detection and mitigation',
        'Enhanced competitive positioning',
        'Data-driven strategic decision making'
      ]
    },
    {
      id: 'client-intelligence',
      title: 'Client Intelligence & Relationship Management',
      description: 'Leverage document intelligence to enhance client relationships and identify growth opportunities.',
      category: 'Client Services',
      complexity: 'Medium',
      keyFeatures: [
        'Automated client portfolio analysis and insights',
        'Personalized research delivery and recommendations',
        'Client risk profile monitoring and alerts',
        'Relationship opportunity identification'
      ],
      businessValue: [
        'Enhanced client advisory capabilities',
        'Proactive client risk management',
        'Increased client satisfaction and retention',
        'New revenue opportunity identification'
      ]
    },
    {
      id: 'credit-intelligence',
      title: 'Credit Intelligence & Risk Assessment',
      description: 'Apply AI-powered document analysis to transform credit decision-making and risk management.',
      category: 'Credit & Lending',
      complexity: 'High',
      keyFeatures: [
        'Automated financial statement analysis',
        'Real-time creditworthiness monitoring',
        'Early warning system for credit deterioration',
        'Regulatory capital optimization'
      ],
      businessValue: [
        'Improved credit decision accuracy',
        'Reduced default rates through early detection',
        'Streamlined loan origination processes',
        'Enhanced regulatory capital efficiency'
      ]
    },
    {
      id: 'operational-intelligence',
      title: 'Operational Intelligence & Automation',
      description: 'Extend document intelligence across operational processes to drive efficiency and reduce manual effort.',
      category: 'Operations',
      complexity: 'Medium',
      keyFeatures: [
        'Automated document processing and classification',
        'Intelligent workflow routing and prioritization',
        'Exception handling and escalation management',
        'Performance analytics and optimization insights'
      ],
      businessValue: [
        'Significant reduction in manual processing costs',
        'Improved operational accuracy and consistency',
        'Enhanced scalability without proportional headcount growth',
        'Better resource allocation and capacity planning'
      ]
    }
  ];

  const getComplexityBadge = (complexity) => {
    const colors = {
      'Low': 'green',
      'Medium': 'blue',
      'High': 'red'
    };
    return <Badge color={colors[complexity]}>{complexity}</Badge>;
  };

  const getCategoryBadge = (category) => {
    return <Badge color="grey" variant="subtle">{category}</Badge>;
  };

  return (
    <Container>
      <SpaceBetween size="l">
        <Box>
          <Header
            variant="h1"
            description="Discover the art of the possible: strategic expansion paths to transform this foundation into enterprise-scale financial intelligence solutions"
          >
            <img 
              src="/avatars/financial-advisor.jpg" 
              alt="Sara Chen" 
              style={{ 
                width: '60px', 
                height: '60px', 
                borderRadius: '50%',
                objectFit: 'cover',
                marginRight: '16px',
                verticalAlign: 'middle'
              }}
            />
            What Should Sara Do Next? The Art of the Possible
          </Header>
        </Box>

        <Alert type="info">
          <Box>
            <TextContent>
              <p>
                Sara has experienced the power of AI-driven document intelligence in action. This foundation can be strategically
                expanded to address critical business challenges across your organization, delivering measurable ROI and
                competitive advantage in today's data-driven financial services landscape.
              </p>
            </TextContent>
          </Box>
        </Alert>

        <Box>
          <Header variant="h2" description="Actionable enhancements to scale this foundation into production-ready capabilities">
            Scaling This Demo Foundation
          </Header>
        </Box>

        <SpaceBetween size="m">
          <Alert type="success">
            <Box>
              <TextContent>
                <p>
                  <strong>🚀 Built to Scale:</strong> The architecture you've just experienced is designed for enterprise expansion. 
                  These actionable enhancements can transform your document intelligence capabilities:
                </p>
              </TextContent>
            </Box>
          </Alert>

          <Cards
            cardDefinition={{
              header: item => (
                <Header variant="h4">{item.title}</Header>
              ),
              sections: [
                {
                  id: "description",
                  content: item => (
                    <Box>
                      <TextContent>
                        <p>{item.description}</p>
                      </TextContent>
                    </Box>
                  )
                }
              ]
            }}
            cardsPerRow={[
              { cards: 1 },
              { minWidth: 600, cards: 2 }
            ]}
            items={[
              {
                title: "📈 Massive Scale Question Processing",
                description: "Scale from 6 questions to hundreds or thousands running simultaneously across your documents. Implement intelligent resource allocation, batching, and real-time progress tracking for enterprise-level research operations."
              },
              {
                title: "🔄 Multi-Dataset Parallel Analysis",
                description: "Run standardised question sets across multiple customer datasets, companies, or time periods simultaneously. Generate comparative analysis and automated reports for each dataset with cross-correlation insights."
              },
              {
                title: "💬 Interactive Chat & Deep Dive",
                description: "Add conversational AI that remembers research context, allowing users to ask follow-up questions, explore results in depth, and refine queries through natural language interactions."
              },
              {
                title: "📊 Automated Data Storytelling",
                description: "Transform raw research findings into compelling narratives with dynamic charts, graphs, and executive summaries tailored for different audiences through interactive dashboards."
              },
              {
                title: "🔗 Multi-Source Data Integration",
                description: "Expand beyond static documents to include real-time market feeds, financial databases, and API connections for comprehensive, up-to-date intelligence across structured and unstructured data."
              }
            ]}
            loadingText="Loading scaling features"
          />
        </SpaceBetween>

        <Box>
          <Header variant="h2" description="Imagine applying this same intelligent document analysis approach across your organization">
            Imagine If You Could...
          </Header>
        </Box>

        <SpaceBetween size="m">
          <Alert type="info">
            <Box>
              <TextContent>
                <p>
                  The document intelligence system you've experienced generates insights from data it can access. But imagine if it could 
                  also take action on those insights, working with other AI agents to automate business processes:
                </p>
              </TextContent>
            </Box>
          </Alert>

          <Cards
            cardDefinition={{
              header: item => (
                <Header variant="h4">{item.title}</Header>
              ),
              sections: [
                {
                  id: "concept",
                  content: item => (
                    <Box>
                      <TextContent>
                        <p style={{ fontStyle: 'italic', marginBottom: '12px' }}>
                          "Imagine if you could {item.concept}..."
                        </p>
                        <p>{item.description}</p>
                      </TextContent>
                    </Box>
                  )
                }
              ]
            }}
            cardsPerRow={[
              { cards: 1 },
              { minWidth: 600, cards: 2 }
            ]}
            items={[
              {
                title: '🏦 Autonomous Portfolio Actions',
                concept: 'have AI agents automatically adjust portfolio allocations based on document intelligence insights',
                description: 'When document analysis detects ESG violations or financial deterioration, AI agents automatically flag holdings for review, generate rebalancing recommendations, and notify portfolio managers with detailed justifications and suggested actions.'
              },
              {
                title: '📋 Self-Executing Compliance',
                concept: 'have regulatory insights automatically trigger compliance workflows and system updates',
                description: 'When document intelligence detects new ASIC or APRA requirements, AI agents automatically update compliance checklists, schedule staff training, create policy drafts, and route approvals through governance workflows.'
              },
              {
                title: '📈 Predictive Market Actions',
                concept: 'have market intelligence automatically generate research reports and client communications',
                description: 'AI agents analyze market filings, detect emerging trends, and automatically generate personalized client alerts, draft research notes, and schedule advisor follow-ups based on client portfolios and risk profiles.'
              },
              {
                title: '🤝 Proactive Client Management',
                concept: 'have client insights automatically trigger personalized outreach and service adjustments',
                description: 'Document analysis identifies client needs or risks, then AI agents automatically schedule advisor meetings, prepare talking points, adjust service levels, and update CRM systems with recommended next actions.'
              },
              {
                title: '💳 Intelligent Credit Decisions',
                concept: 'have credit analysis automatically approve standard loans or escalate complex cases',
                description: 'AI agents analyze financial documents, make preliminary credit decisions within defined parameters, automatically approve qualifying applications, and route complex cases to human underwriters with detailed analysis and recommendations.'
              },
              {
                title: '⚙️ Self-Optimizing Operations',
                concept: 'have operational insights automatically improve processes and resource allocation',
                description: 'Document intelligence identifies inefficiencies, then AI agents automatically adjust workflows, reallocate resources, update process documentation, and coordinate with other systems to optimize performance without human intervention.'
              }
            ]}
            loadingText="Loading automation concepts"
          />
        </SpaceBetween>

       {/*  <Box>
          <Header variant="h2">Implementation Strategy</Header>
          <SpaceBetween size="m">
            <Alert type="success">
              <Box>
                <Header variant="h4">Proven Foundation</Header>
                <TextContent>
                  <ul>
                    <li><strong>Enterprise-Grade AI Architecture:</strong> Built on AWS Bedrock with Strands multi-agent orchestration</li>
                    <li><strong>Production-Ready Infrastructure:</strong> Scalable, secure, and compliant with financial services requirements</li>
                    <li><strong>Proven Document Intelligence:</strong> Advanced PDF processing and knowledge extraction capabilities</li>
                    <li><strong>Extensible Design:</strong> Modular architecture enables rapid expansion to new use cases</li>
                    <li><strong>AWS Native:</strong> Leverages AWS services for security, compliance, and global scale</li>
                  </ul>
                </TextContent>
              </Box>
            </Alert>

            <Alert type="info">
              <Box>
                <Header variant="h4">Path to Production</Header>
                <TextContent>
                  <ol>
                    <li><strong>Strategic Assessment:</strong> Identify high-impact use cases aligned with your business priorities</li>
                    <li><strong>Architecture Workshop:</strong> Design scalable solutions leveraging AWS best practices</li>
                    <li><strong>Pilot Implementation:</strong> Rapid prototyping with measurable success criteria</li>
                    <li><strong>Production Deployment:</strong> Enterprise-grade implementation with full compliance and security</li>
                    <li><strong>Continuous Innovation:</strong> Ongoing enhancement and expansion to new capabilities</li>
                  </ol>
                </TextContent>
              </Box>
            </Alert>
          </SpaceBetween>
        </Box> */}

        <Box>
          <Header variant="h2" description="Ready to explore how to build these solutions? Start with our comprehensive technical deep dive and hands-on workshops.">
            Ready to Get Started?
          </Header>
          <Button
            variant="primary"
            iconName="arrow-right"
            onClick={() => window.location.href = '/deep-dive/strands-agents'}
          >
            Explore Strands Agents Deep Dive
          </Button>
        </Box>
      </SpaceBetween>
    </Container>
  );
}

export default WhatsNextSection;